import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mic, MicOff, Zap, ShieldCheck, Download, Battery, Info } from 'lucide-react';

const features = [
  {
    icon: MicOff,
    title: 'Mute from your headphones',
    description:
      'Press the AirPods stem, or the noise-cancelling button on Sony WH-1000XM4, to mute and unmute mid-call — no reaching for the trackpad or fumbling for the right app window.',
  },
  {
    icon: Zap,
    title: 'Works in every call app',
    description:
      'Meet, Zoom, Teams, Slack, FaceTime — the mute happens at the system level, on whichever microphone your call is actually using. The menu bar icon is always the source of truth for whether you are live.',
  },
  {
    icon: Battery,
    title: 'Battery & device info at a glance',
    description:
      'Click the menu bar icon for battery, firmware and connection details without opening System Settings — including Sony headsets, which macOS reports no battery for at all.',
  },
  {
    icon: ShieldCheck,
    title: 'Lives quietly in your menu bar',
    description:
      'No dock icon, no windows staying open. It sits in the menu bar and does one job well.',
  },
];

const requirements = [
  'macOS 14 (Sonoma) or later',
  'Apple Silicon or Intel Mac',
  'AirPods with mute-gesture support (stem press), or Sony WH-1000XM4',
  'Microphone permission (only used to detect the gesture during calls)',
  'Bluetooth permission (Sony headsets only — for battery and button presses)',
];

export default function SeaCloudMute() {
  useEffect(() => {
    // Keep in sync with the prerendered <title> in scripts/prerender.js.
    document.title = 'SeaCloud Mute — Mute your call from your headphones | Free for macOS';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="product-page">
      <div className="product-hero">
        <div className="section-inner product-hero-inner">
          <Link to="/" className="legal-back product-back">← Back to SeaCloud Software</Link>

          <div className="product-hero-icon">
            <Mic size={28} strokeWidth={1.8} />
          </div>

          <h1 className="product-hero-title">SeaCloud Mute</h1>
          <p className="product-hero-tagline">
            Mute and unmute your call from your headphones. Free, for macOS.
          </p>
          <p className="product-hero-sub">
            A tiny menu bar app that listens for your headphones' mute gesture during calls and
            mutes your microphone system-wide — so the button on your headphones actually does
            something, in whichever app you are calling from.
          </p>

          <span className="product-coming-soon">
            <Download size={18} strokeWidth={2} />
            Coming soon
          </span>
          <p className="product-hero-meta">
            Free · macOS 14+ · Apple Silicon &amp; Intel · currently in testing — check back shortly
          </p>
        </div>
      </div>

      <div className="section-inner">
        <section className="product-features">
          <div className="product-features-grid">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="service-card">
                  <div className="service-icon-wrap">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <h3 className="service-card-title">{f.title}</h3>
                  <p className="service-card-desc">{f.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="product-install">
          <h2 className="product-section-title">First launch (one-time)</h2>
          <p className="product-section-sub">
            SeaCloud Mute isn't yet notarized through Apple's paid Developer Program, so the first
            time you open it macOS says <em>“Apple could not verify &quot;SeaCloud Mute&quot; is free
            of malware that may harm your Mac or compromise your privacy.”</em> and offers only
            <strong>Move to Trash</strong> or <strong>Done</strong>. That's expected — here's how to
            get past it in a few seconds:
          </p>

          <ol className="product-steps">
            <li>
              <span className="product-step-num">1</span>
              <span>Unzip the download, then move <strong>SeaCloud Mute.app</strong> to your Applications folder.</span>
            </li>
            <li>
              <span className="product-step-num">2</span>
              <span>Double-click it. macOS refuses the first time — that is the check working, not a fault.</span>
            </li>
            <li>
              <span className="product-step-num">3</span>
              <span>
                Open <strong>System Settings → Privacy &amp; Security</strong>, scroll to
                <strong> Security</strong>, and click <strong>Open Anyway</strong> next to SeaCloud
                Mute. On macOS 14 Sonoma you can instead right-click the app and choose{' '}
                <strong>Open</strong>. Either way, macOS remembers after the first launch.
              </span>
            </li>
            <li>
              <span className="product-step-num">4</span>
              <span>Grant Microphone access when prompted — that's how it hears the mute gesture during a call.</span>
            </li>
          </ol>

          <div className="product-note">
            <Info size={16} strokeWidth={2} />
            <span>
              We're planning to move to a notarized, Gatekeeper-friendly build soon. Until then,
              this one-time approval is the only friction. Need more detail?{' '}
              <Link to="/seacloud-mute/install" className="legal-link">
                Read the full install guide
              </Link>.
            </span>
          </div>
        </section>

        <section className="product-requirements">
          <h2 className="product-section-title">Requirements</h2>
          <ul className="product-req-list">
            {requirements.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </section>

        <section className="product-footer-cta">
          <p>
            Questions, bugs, or feature requests?{' '}
            <a href="mailto:hello@seacloud-software.hr" className="legal-link">
              hello@seacloud-software.hr
            </a>
          </p>
          <p className="product-footer-note">
            Built by <Link to="/" className="legal-link">SeaCloud Software</Link> — we also build
            production-grade software for founders and CTOs.
          </p>
        </section>
      </div>
    </div>
  );
}
